'use client';

import React, { useState, useEffect } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@/components/Icons/Icons';

interface PaginationComponentProps {
  currentPage: number; // 0-indexed
  totalPages: number;
  onPageChange: (page: number) => void;
}

export default function PaginationComponent({
  currentPage,
  totalPages,
  onPageChange,
}: PaginationComponentProps) {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  if (totalPages <= 1) {
    return null;
  }

  const getPages = (isMobile: boolean = false): (number | string)[] => {
    const pages: (number | string)[] = [];
    const lastPage = totalPages - 1;

    if (isMobile) {
      if (totalPages <= 5) {
        for (let i = 0; i < totalPages; i++) pages.push(i);
      } else if (currentPage <= 2) {
        for (let i = 0; i <= 2; i++) pages.push(i);
        pages.push('...');
        pages.push(lastPage);
      } else if (currentPage >= lastPage - 2) {
        pages.push(0);
        pages.push('...');
        for (let i = lastPage - 2; i <= lastPage; i++) pages.push(i);
      } else {
        pages.push(0);
        pages.push('...');
        pages.push(currentPage);
        pages.push('...');
        pages.push(lastPage);
      }
    } else {
      if (totalPages <= 7) {
        for (let i = 0; i < totalPages; i++) pages.push(i);
      } else if (currentPage <= 3) {
        for (let i = 0; i <= 4; i++) pages.push(i);
        pages.push('...');
        pages.push(lastPage);
      } else if (currentPage >= lastPage - 3) {
        pages.push(0);
        pages.push('...');
        for (let i = lastPage - 4; i <= lastPage; i++) pages.push(i);
      } else {
        pages.push(0);
        pages.push('...');
        pages.push(currentPage - 1);
        pages.push(currentPage);
        pages.push(currentPage + 1);
        pages.push('...');
        pages.push(lastPage);
      }
    }
    return pages;
  };

  const pages = getPages(isMobile);

  const handlePageClick = (page: number) => {
    if (page !== currentPage && page >= 0 && page < totalPages) {
      onPageChange(page);
    }
  };

  const handlePrevious = () => {
    if (currentPage > 0) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages - 1) {
      onPageChange(currentPage + 1);
    }
  };

  return (
    <nav
      role="navigation"
      aria-label="Pagination Navigation"
      className="flex items-center justify-center gap-1 md:gap-2 py-6"
    >
      <button
        onClick={handlePrevious}
        disabled={currentPage === 0}
        aria-label="Go to previous page"
        className={`
          flex items-center justify-center
          h-10 w-10 md:h-12 md:w-12
          rounded-lg
          bg-[var(--Surfaces-Surface-3)]
          transition-all duration-200
          ${
            currentPage === 0
              ? 'opacity-30 cursor-not-allowed'
              : 'hover:bg-[var(--Surfaces-Surface-4)] active:scale-95 cursor-pointer'
          }
        `}
      >
        <ChevronLeftIcon 
          className="text-[var(--Text-Default-Strong)]" 
          size={isMobile ? 20 : 24}
        />
      </button>

      <div className="flex items-center gap-1 md:gap-2">
        {pages.map((page, index) => {
          if (typeof page === 'string') {
            return (
              <button
                key={`ellipsis-${index}`}
                disabled
                aria-label="More pages"
                className="
                  flex items-center justify-center
                  h-10 w-10 md:h-12 md:w-12
                  text-[var(--Text-Default-Medium)]
                  cursor-default
                  select-none
                  rounded-lg
                  font-medium
                  text-sm md:text-base
                "
              >
                ...
              </button>
            );
          }

          const isActive = page === currentPage;
          const displayPage = page + 1;

          return (
            <button
              key={page}
              onClick={() => handlePageClick(page)}
              aria-label={`Go to page ${displayPage}`}
              aria-current={isActive ? 'page' : undefined}
              className={`
                flex items-center justify-center
                h-10 w-10 md:h-12 md:w-12
                rounded-lg
                font-medium
                text-sm md:text-base
                transition-all duration-200
                ${
                  isActive
                    ? 'bg-[var(--Color-Extended-Amethyst-Amethyst-Accent)] text-[var(--Text-Inverse-Strong)]'
                    : 'text-[var(--Text-Default-Strong)] hover:bg-[var(--Surfaces-Surface-3)] active:scale-95'
                }
              `}
            >
              {displayPage}
            </button>
          );
        })}
      </div>

      <button
        onClick={handleNext}
        disabled={currentPage === totalPages - 1}
        aria-label="Go to next page"
        className={`
          flex items-center justify-center
          h-10 w-10 md:h-12 md:w-12
          rounded-lg
          bg-[var(--Surfaces-Surface-3)]
          transition-all duration-200
          ${
            currentPage === totalPages - 1
              ? 'opacity-30 cursor-not-allowed'
              : 'hover:bg-[var(--Surfaces-Surface-4)] active:scale-95 cursor-pointer'
          }
        `}
      >
        <ChevronRightIcon 
          className="text-[var(--Text-Default-Strong)]" 
          size={isMobile ? 20 : 24}
        />
      </button>
    </nav>
  );
}
